import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Ticket, Plus, Search, Clock, User,
  CheckCircle, Circle, Pause, ChevronRight, Mail, Settings, Trash2, Square, CheckSquare, Paperclip,
  GitMerge, ArrowRight, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_ICONS = {
  'Open': <Circle className="h-3 w-3 text-blue-500" />,
  'In Progress': <Clock className="h-3 w-3 text-yellow-500" />,
  'Waiting': <Pause className="h-3 w-3 text-orange-500" />,
  'Resolved': <CheckCircle className="h-3 w-3 text-green-500" />,
  'Closed': <CheckCircle className="h-3 w-3 text-gray-500" />,
  'Merged': <GitMerge className="h-3 w-3 text-violet-500" />,
};

const STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'Waiting': 'bg-orange-100 text-orange-700',
  'Resolved': 'bg-green-100 text-green-700',
  'Closed': 'bg-gray-100 text-gray-600',
  'Merged': 'bg-violet-100 text-violet-700',
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-600',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Critical': 'bg-red-100 text-red-700',
};

const formatTime = (minutes) => {
  if (!minutes) return '-';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
};

const formatDate = (isoString) => {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

const ManagedTicketsPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    assigned_to: '',
    priority: '',
    search: '',
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [merging, setMerging] = useState(false);
  const [primaryTicketId, setPrimaryTicketId] = useState(null);
  const [showMerged, setShowMerged] = useState(false);

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchSettings();
  }, [filters, page, showMerged]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedTickets(new Set());
  }, [filters, page]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.assigned_to && filters.assigned_to !== 'all') params.assigned_to = filters.assigned_to;
      if (filters.priority && filters.priority !== 'all') params.priority = filters.priority;
      if (filters.search) params.search = filters.search;
      if (showMerged) params.show_merged = true;
      
      const res = await api.getManagedTickets(params);
      setTickets(res.data.tickets);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.getManagedStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.getTicketSettings();
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
    setPage(1);
  };

  const handlePollEmails = async () => {
    try {
      await api.pollEmails();
      toast.success('Checking for new emails...');
      setTimeout(() => {
        fetchTickets();
        fetchStats();
      }, 2000);
    } catch (err) {
      toast.error('Failed to check emails');
    }
  };

  // Bulk selection handlers
  const toggleSelectTicket = (ticketId, e) => {
    e.stopPropagation();
    setSelectedTickets(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTickets.size === tickets.length) {
      setSelectedTickets(new Set());
    } else {
      setSelectedTickets(new Set(tickets.map(t => t.ticket_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTickets.size === 0) return;
    
    const confirmMsg = `Are you sure you want to delete ${selectedTickets.size} ticket(s)? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setDeleting(true);
    let deleted = 0;
    let failed = 0;

    for (const ticketId of selectedTickets) {
      try {
        await api.deleteManagedTicket(ticketId);
        deleted++;
      } catch (err) {
        failed++;
        console.error(`Failed to delete ${ticketId}:`, err);
      }
    }

    setDeleting(false);
    setSelectedTickets(new Set());
    
    if (deleted > 0) {
      toast.success(`Deleted ${deleted} ticket(s)`);
      fetchTickets();
      fetchStats();
    }
    if (failed > 0) {
      toast.error(`Failed to delete ${failed} ticket(s)`);
    }
  };

  const openMergeDialog = () => {
    if (selectedTickets.size < 2) {
      toast.error('Select at least 2 tickets to merge');
      return;
    }
    // Default primary = first selected
    const firstId = [...selectedTickets][0];
    setPrimaryTicketId(firstId);
    setShowMergeDialog(true);
  };

  const handleMerge = async () => {
    if (!primaryTicketId) return;
    const secondaryIds = [...selectedTickets].filter(id => id !== primaryTicketId);
    if (secondaryIds.length === 0) {
      toast.error('Select a primary ticket different from secondaries');
      return;
    }
    setMerging(true);
    try {
      const res = await api.mergeTickets(primaryTicketId, secondaryIds);
      toast.success(res.data.message);
      setShowMergeDialog(false);
      setSelectedTickets(new Set());
      fetchTickets();
      fetchStats();
    } catch (err) {
      console.error('Merge failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to merge tickets');
    } finally {
      setMerging(false);
    }
  };

  const selectedTicketsList = tickets.filter(t => selectedTickets.has(t.ticket_id));

  const allSelected = tickets.length > 0 && selectedTickets.size === tickets.length;
  const someSelected = selectedTickets.size > 0 && selectedTickets.size < tickets.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_helpdesk-hub-53/artifacts/dtuif3s8_NEWGamerLogo.jpg"
              alt="Gamer Packaging"
              className="h-10 object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
            <div className="h-8 w-px bg-slate-200" />
            <div className="p-2 bg-blue-100 rounded-lg">
              <Ticket className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800">Ticket Management</h1>
              <p className="text-xs text-slate-400">Managed Ticketing System</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/tickets/settings')} className="gap-2" data-testid="settings-btn">
              <Settings className="h-4 w-4" />
            </Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={() => navigate('/tickets/new')}>
              <Plus className="h-4 w-4" />
              New Ticket
            </Button>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card className="cursor-pointer hover:shadow-md" onClick={() => handleFilterChange('status', 'all')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => handleFilterChange('status', 'Open')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.open}</p>
                <p className="text-xs text-gray-500">Open</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => handleFilterChange('status', 'In Progress')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.in_progress || 0}</p>
                <p className="text-xs text-gray-500">In Progress</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => handleFilterChange('status', 'Waiting')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{stats.waiting}</p>
                <p className="text-xs text-gray-500">Waiting</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => handleFilterChange('status', 'Closed')}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.closed}</p>
                <p className="text-xs text-gray-500">Closed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-600">{formatTime(stats.avg_resolution_minutes)}</p>
                <p className="text-xs text-gray-500">Avg Resolve</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search tickets..."
                    className="pl-10"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>
              </div>
              <Select value={filters.status || 'all'} onValueChange={(v) => handleFilterChange('status', v)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Waiting">Waiting</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.assigned_to || 'all'} onValueChange={(v) => handleFilterChange('assigned_to', v)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {settings?.it_staff?.filter(s => s && s.trim()).map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.priority || 'all'} onValueChange={(v) => handleFilterChange('priority', v)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => { fetchTickets(); fetchStats(); }}>
                Refresh
              </Button>
              <Button 
                variant={showMerged ? "default" : "outline"} 
                size="sm" 
                onClick={() => setShowMerged(!showMerged)}
                className={showMerged ? "gap-2 bg-violet-600 hover:bg-violet-700" : "gap-2 text-violet-600 border-violet-200 hover:bg-violet-50"}
                data-testid="show-merged-toggle"
              >
                <GitMerge className="h-4 w-4" />
                {showMerged ? 'Hide Merged' : 'Show Merged'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {selectedTickets.size > 0 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 flex items-center justify-between">
              <span className="text-sm font-medium text-blue-800">
                {selectedTickets.size} ticket(s) selected
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setSelectedTickets(new Set())}
                >
                  Clear Selection
                </Button>
                {selectedTickets.size >= 2 && (
                  <Button
                    size="sm"
                    onClick={openMergeDialog}
                    className="gap-2 bg-violet-600 hover:bg-violet-700"
                    data-testid="merge-tickets-btn"
                  >
                    <GitMerge className="h-4 w-4" />
                    Merge {selectedTickets.size} Tickets
                  </Button>
                )}
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? 'Deleting...' : `Delete ${selectedTickets.size}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ticket List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : tickets.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Ticket className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No tickets found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {/* Select All Header */}
                <div className="p-3 bg-gray-50 flex items-center gap-4 border-b">
                  <div 
                    className="cursor-pointer p-1"
                    onClick={toggleSelectAll}
                  >
                    {allSelected ? (
                      <CheckSquare className="h-5 w-5 text-blue-600" />
                    ) : someSelected ? (
                      <div className="h-5 w-5 border-2 border-blue-600 rounded flex items-center justify-center">
                        <div className="h-2 w-2 bg-blue-600 rounded-sm" />
                      </div>
                    ) : (
                      <Square className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <span className="text-sm text-gray-600">
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </span>
                </div>

                {/* Ticket Rows */}
                {tickets.map(ticket => (
                  <div
                    key={ticket.ticket_id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer flex items-center gap-4 ${
                      selectedTickets.has(ticket.ticket_id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <div 
                      className="flex-shrink-0 cursor-pointer p-1"
                      onClick={(e) => toggleSelectTicket(ticket.ticket_id, e)}
                    >
                      {selectedTickets.has(ticket.ticket_id) ? (
                        <CheckSquare className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </div>

                    {/* Status Icon */}
                    <div className="flex-shrink-0">
                      {STATUS_ICONS[ticket.status] || <Circle className="h-3 w-3" />}
                    </div>

                    {/* Ticket Info - Clickable */}
                    <div 
                      className="flex-1 min-w-0"
                      onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">#{ticket.ticket_number}</span>
                        <h3 className="font-medium text-gray-900 truncate">{ticket.summary}</h3>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{ticket.created_by_name || ticket.created_by_email}</span>
                        <span>•</span>
                        <span>{formatDate(ticket.created_on)}</span>
                        {ticket.time_spent > 0 && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(ticket.time_spent)}
                            </span>
                          </>
                        )}
                        {ticket.merged_into_number && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-violet-600">
                              <GitMerge className="h-3 w-3" />
                              Merged into #{ticket.merged_into_number}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Badges */}
                    <div 
                      className="flex items-center gap-2"
                      onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                    >
                      {/* Attachment indicator */}
                      {(() => {
                        const ticketAttachments = ticket.attachments?.length || 0;
                        const noteAttachments = ticket.notes?.reduce((sum, note) => sum + (note.attachments?.length || 0), 0) || 0;
                        const totalAttachments = ticketAttachments + noteAttachments;
                        return totalAttachments > 0 ? (
                          <Badge className="text-xs bg-gray-100 text-gray-600 gap-1" data-testid={`attachment-badge-${ticket.ticket_id}`}>
                            <Paperclip className="h-3 w-3" />
                            {totalAttachments}
                          </Badge>
                        ) : null;
                      })()}
                      <Badge className={`text-xs ${STATUS_COLORS[ticket.status] || ''}`}>
                        {ticket.status}
                      </Badge>
                      <Badge className={`text-xs ${PRIORITY_COLORS[ticket.priority] || ''}`}>
                        {ticket.priority}
                      </Badge>
                      {ticket.assigned_to && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <User className="h-3 w-3" />
                          {ticket.assigned_to}
                        </div>
                      )}
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="py-2 px-4 text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-lg" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-violet-600" />
              Merge Tickets
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select which ticket should be the <strong>primary</strong>. All notes, time, and history from the other tickets will be merged into it. The other tickets will be closed with "Merged" status.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {selectedTicketsList.map(t => (
                <div
                  key={t.ticket_id}
                  onClick={() => setPrimaryTicketId(t.ticket_id)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    primaryTicketId === t.ticket_id
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  data-testid={`merge-option-${t.ticket_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {primaryTicketId === t.ticket_id && (
                        <Badge className="bg-violet-600 text-white text-xs">Primary</Badge>
                      )}
                      <span className="text-sm font-medium text-gray-500">#{t.ticket_number}</span>
                      <span className="text-sm font-medium truncate max-w-48">{t.summary}</span>
                    </div>
                    <Badge className={`text-xs ${STATUS_COLORS[t.status] || ''}`}>{t.status}</Badge>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t.created_by_name || t.created_by_email} &middot; {formatDate(t.created_on)}
                    {t.notes?.length > 0 && ` \u00B7 ${t.notes.length} note(s)`}
                  </div>
                  {primaryTicketId !== t.ticket_id && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                      <ArrowRight className="h-3 w-3" />
                      Will be merged into primary
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>Cancel</Button>
            <Button
              onClick={handleMerge}
              disabled={merging || !primaryTicketId}
              className="gap-2 bg-violet-600 hover:bg-violet-700"
              data-testid="confirm-merge-btn"
            >
              {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
              {merging ? 'Merging...' : `Merge into #${selectedTicketsList.find(t => t.ticket_id === primaryTicketId)?.ticket_number || ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagedTicketsPage;
